<template>
  <div class="as-page">
    <div class="as-filters">
      <v-select
        v-model="partnerId"
        :items="partners"
        item-text="name"
        item-value="id"
        label="Partner"
        outlined
        dense
        hide-details
        :disabled="loading"
        class="as-field"
        @change="load"
      />

      <v-chip
        v-if="partnerId && config"
        x-small
        label
        :color="config.is_saved ? 'success' : 'warning'"
        text-color="white"
        class="ml-1"
      >
        {{ config.is_saved ? "Configured" : "Using note defaults" }}
      </v-chip>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!isDirty"
        :loading="saving"
        @click="save"
      >
        Save
      </v-btn>
      <v-btn
        color="primary"
        class="ml-1"
        :disabled="!partnerId || isDirty"
        @click="goToApportionment"
      >
        Summary
      </v-btn>
    </div>

    <div v-if="loading" class="as-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="as-state__text">Loading…</p>
    </div>

    <v-card v-else-if="!partnerId" outlined class="as-state as-state--card">
      <p class="as-state__text">
        Select a partner to view and edit the apportionment share...
      </p>
    </v-card>

    <template v-else-if="form">
      <v-card outlined class="as-card">
        <div class="as-card__head">
          <h3 class="as-card__title">
            1st Semester · Higher Semester · Other Fee
          </h3>
          <p class="as-card__sub">
            The note applies the same two charges to all three.
          </p>
        </div>

        <div class="as-rules">
          <div class="as-rule">
            <span class="as-rule__no">1</span>
            <div class="as-rule__body">
              <p class="as-rule__text">
                On the monthly apportioned fee
                <v-text-field
                  v-model.number="form.education_rate"
                  suffix="%"
                  type="number"
                  min="0"
                  max="100"
                  outlined
                  dense
                  hide-details
                  class="as-inline"
                />
                will be <strong>Education Support</strong> gross revenue.
              </p>
            </div>
          </div>

          <div class="as-rule">
            <span class="as-rule__no">2</span>
            <div class="as-rule__body">
              <p class="as-rule__text">
                To arrive at Education Support net revenue, GST of
                <v-text-field
                  v-model.number="form.education_gst_percentage"
                  suffix="%"
                  type="number"
                  min="0"
                  max="100"
                  outlined
                  dense
                  hide-details
                  class="as-inline"
                />
                is
                <v-btn-toggle
                  v-model="form.education_gst_type"
                  mandatory
                  dense
                  class="as-toggle"
                >
                  <v-btn x-small value="Inclusive">Inclusive</v-btn>
                  <v-btn x-small value="Exclusive">Exclusive</v-btn>
                </v-btn-toggle>
              </p>
              <p class="as-rule__effect">{{ educationEffect }}</p>
            </div>
          </div>

          <div class="as-rule">
            <span class="as-rule__no">3</span>
            <div class="as-rule__body">
              <p class="as-rule__text">
                On the monthly apportioned fee
                <v-text-field
                  v-model.number="form.exam_rate"
                  suffix="%"
                  type="number"
                  min="0"
                  max="100"
                  outlined
                  dense
                  hide-details
                  class="as-inline"
                />
                will be <strong>Exam Support</strong> net revenue.
              </p>
            </div>
          </div>
        </div>
      </v-card>

      <v-card outlined class="as-card">
        <div class="as-card__head">
          <h3 class="as-card__title">New Admission</h3>
          <p class="as-card__sub">
            Charged <em>instead of</em> the two above, not on top of them.
          </p>
        </div>

        <div class="as-rules">
          <div class="as-rule">
            <span class="as-rule__no">1</span>
            <div class="as-rule__body">
              <v-checkbox
                v-model="form.new_admission_included"
                label="Is New Admission included for this partner?"
                dense
                hide-details
                class="as-check"
              />

              <template v-if="form.new_admission_included">
                <p class="as-rule__text mt-3">
                  On the monthly apportioned fee
                  <v-text-field
                    v-model.number="form.new_admission_rate"
                    suffix="%"
                    type="number"
                    min="0"
                    max="100"
                    outlined
                    dense
                    hide-details
                    class="as-inline"
                  />
                  will be <strong>Admission Support</strong> net revenue.
                </p>
                <p class="as-rule__effect">{{ admissionEffect }}</p>
              </template>
              <p v-else class="as-rule__effect">
                Not paid for admission — a new admission earns the same as any
                other enrolment ({{ form.education_rate }}% +
                {{ form.exam_rate }}%).
              </p>
            </div>
          </div>
        </div>
      </v-card>

      <v-card outlined class="as-card">
        <div class="as-card__head">
          <h3 class="as-card__title">Check a fee</h3>
          <p class="as-card__sub">
            Put in any amount and the rates above tell you what it earns — the
            same arithmetic the apportionment runs.
          </p>
        </div>

        <div class="as-check-row">
          <v-text-field
            v-model.number="checker.amount"
            label="Amount collected"
            prefix="₹"
            type="number"
            min="0"
            outlined
            dense
            hide-details
            class="as-check-field"
          />
          <v-select
            v-model.number="checker.months"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            label="Plan"
            outlined
            dense
            hide-details
            class="as-check-field"
          />
          <v-select
            v-model="checker.bucket"
            :items="bucketOptions"
            item-text="text"
            item-value="value"
            label="Group"
            outlined
            dense
            hide-details
            class="as-check-field"
          />
        </div>

        <div class="as-scroll">
          <table class="as-table">
            <tbody>
              <tr v-for="line in checkResult.lines" :key="line.label">
                <td class="as-td">{{ line.label }}</td>
                <td class="as-td as-td--muted">{{ line.working }}</td>
                <td class="as-td as-td--num">{{ money(line.net) }}</td>
              </tr>
              <tr>
                <td class="as-td as-td--strong">Revenue per month</td>
                <td class="as-td as-td--muted">
                  {{ money(checker.amount) }} ÷ {{ checker.months }} =
                  {{ money(checkResult.monthly) }} per month
                </td>
                <td class="as-td as-td--num as-td--strong">
                  {{ money(checkResult.net) }}
                </td>
              </tr>
              <tr>
                <td class="as-td as-td--strong">
                  Total over {{ checker.months }} months
                </td>
                <td class="as-td as-td--muted">
                  {{ money(checkResult.net) }} × {{ checker.months }}
                </td>
                <td class="as-td as-td--num as-td--strong">
                  {{ money(checkResult.total) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>
    </template>
  </div>
</template>

<script>
function money(value) {
  return Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default {
  name: "RevenueApportionmentSetup",

  data() {
    return {
      partners: [],
      partnerId: null,
      config: null,
      form: null,
      snapshot: "",
      loading: false,
      saving: false,

      checker: { amount: 48000, months: 12, bucket: "ongoing" },
    };
  },

  computed: {
    isDirty() {
      return Boolean(this.form) && JSON.stringify(this.form) !== this.snapshot;
    },

    educationEffect() {
      const rate = Number(this.form.education_rate) || 0;
      const gst = Number(this.form.education_gst_percentage) || 0;

      if (!gst) return `Net equals gross — ${rate}% of the fee.`;
      if (this.form.education_gst_type === "Inclusive") {
        return `Net = fee × ${rate}% ÷ ${(1 + gst / 100).toFixed(2)}`;
      }
      return `Net = fee × ${rate}%, with ${gst}% GST added on top.`;
    },

    admissionEffect() {
      const rate = Number(this.form.new_admission_rate) || 0;
      return `Net = fee × ${rate}%, and neither Education nor Exam Support applies.`;
    },

    monthOptions() {
      return [
        { text: "Sem Wise · 6 months", value: 6 },
        { text: "Annual · 12 months", value: 12 },
        { text: "Full · 24 months", value: 24 },
        { text: "Other fee · 1 month", value: 1 },
      ];
    },

    bucketOptions() {
      return [
        {
          text: "1st Semester / Higher Semester / Other Fee",
          value: "ongoing",
        },
        { text: "New Admission", value: "new_admission" },
      ];
    },

    checkResult() {
      const amount = Number(this.checker.amount) || 0;
      const months = Number(this.checker.months) || 1;
      const monthly = Math.round((amount / months) * 100) / 100;

      const net = (rate, gst, type) => {
        const gross = (monthly * (Number(rate) || 0)) / 100;
        const percentage = Number(gst) || 0;
        return type === "Inclusive" ? gross / (1 + percentage / 100) : gross;
      };

      const admission =
        this.checker.bucket === "new_admission" &&
        this.form.new_admission_included;

      const lines = admission
        ? [
            {
              label: "Admission Support",
              working: `${money(monthly)} × ${this.form.new_admission_rate}%`,
              net: net(
                this.form.new_admission_rate,
                this.form.new_admission_gst_percentage,
                this.form.new_admission_gst_type,
              ),
            },
          ]
        : [
            {
              label: "Education Support",
              working:
                `${money(monthly)} × ${this.form.education_rate}%` +
                (this.form.education_gst_type === "Inclusive" &&
                Number(this.form.education_gst_percentage)
                  ? ` ÷ ${(
                      1 +
                      Number(this.form.education_gst_percentage) / 100
                    ).toFixed(2)}`
                  : ""),
              net: net(
                this.form.education_rate,
                this.form.education_gst_percentage,
                this.form.education_gst_type,
              ),
            },
            {
              label: "Exam Support",
              working: `${money(monthly)} × ${this.form.exam_rate}%`,
              net: net(
                this.form.exam_rate,
                this.form.exam_gst_percentage,
                this.form.exam_gst_type,
              ),
            },
          ];

      const total = lines.reduce((sum, line) => sum + line.net, 0);
      const perMonth = Math.round(total * 100) / 100;

      return {
        monthly,
        lines,
        net: perMonth,
        total: Math.round(perMonth * months * 100) / 100,
      };
    },

    examples() {
      const edu = Number(this.form.education_rate) || 0;
      const gst = Number(this.form.education_gst_percentage) || 0;
      const exam = Number(this.form.exam_rate) || 0;
      const divisor =
        this.form.education_gst_type === "Inclusive" ? 1 + gst / 100 : 1;

      const ongoing = (fee) => (fee * edu) / 100 / divisor + (fee * exam) / 100;

      const higherFee = Math.round((70000 / 12) * 100) / 100;
      const admissionFee = Math.round((20000 / 6) * 100) / 100;
      const admissionRate = Number(this.form.new_admission_rate) || 0;

      const rows = [
        {
          label: "₹70,000 annual, higher semester",
          months: 12,
          monthly: higherFee,
          formula: `${edu}% ÷ ${divisor.toFixed(2)} + ${exam}%`,
          net: ongoing(higherFee),
        },
      ];

      rows.push(
        this.form.new_admission_included
          ? {
              label: "₹20,000 semester, new admission",
              months: 6,
              monthly: admissionFee,
              formula: `${admissionRate}%`,
              net: (admissionFee * admissionRate) / 100,
            }
          : {
              label: "₹20,000 semester, new admission",
              months: 6,
              monthly: admissionFee,
              formula: `${edu}% ÷ ${divisor.toFixed(
                2,
              )} + ${exam}% (admission not included)`,
              net: ongoing(admissionFee),
            },
      );

      return rows;
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Revenue Recognition" }]);
    this.initialise();
  },

  methods: {
    async initialise() {
      await this.getPartners();

      const partnerId = Number(this.$route.query.partner_id);
      if (
        partnerId &&
        this.partners.some((partner) => partner.id === partnerId)
      ) {
        this.partnerId = partnerId;
        await this.load();
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");

        this.partners = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async load() {
      if (!this.partnerId) return;

      this.loading = true;
      try {
        const res = await this.$axios.get("/revenue_apportionment_config", {
          params: { partner_id: this.partnerId },
        });
        this.config = res.data.data;
        this.form = {
          education_rate: Number(this.config.education_rate),
          education_gst_percentage: Number(
            this.config.education_gst_percentage,
          ),
          education_gst_type: this.config.education_gst_type,
          exam_rate: Number(this.config.exam_rate),
          exam_gst_percentage: Number(this.config.exam_gst_percentage),
          exam_gst_type: this.config.exam_gst_type,
          new_admission_included: this.config.new_admission_included,
          new_admission_rate: Number(this.config.new_admission_rate),
          new_admission_gst_percentage: Number(
            this.config.new_admission_gst_percentage,
          ),
          new_admission_gst_type: this.config.new_admission_gst_type,
        };
        this.snapshot = JSON.stringify(this.form);
      } catch (err) {
        this.notifyError(err, "Unable to load the configuration");
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        await this.$axios.put("/revenue_apportionment_config", {
          partner_id: this.partnerId,
          config: this.form,
        });

        this.$store.commit("showSnackbar", {
          message: "Recognition rates saved",
          color: "success",
        });

        await this.load();
      } catch (err) {
        this.notifyError(err, "Unable to save the configuration");
      } finally {
        this.saving = false;
      }
    },

    goToApportionment() {
      this.$router.push({
        name: "Revenue Apportionment",
        query: { partner_id: this.partnerId },
      });
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changeset = data?.errors
        ? Object.entries(data.errors)
            .map(
              ([field, messages]) =>
                `${field} ${[].concat(messages).join(", ")}`,
            )
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changeset || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.as-page {
  --as-line: #e4e7ec;
  --as-ink: #101828;
  --as-ink-2: #475467;
  --as-ink-3: #98a2b3;
  padding: 16px 20px 40px;
}

.as-header {
  margin-bottom: 14px;
}

.as-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--as-ink);
  letter-spacing: -0.2px;
}

.as-header__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--as-ink-2);
}

.as-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
}

.as-field {
  max-width: 240px;
  flex: 0 1 240px;
}

.as-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 44px 20px;
}

.as-state--card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
}

.as-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--as-ink);
}

.as-state__text {
  margin: 0;
  font-size: 13px;
  color: var(--as-ink-2);
}

.as-card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.as-card__head {
  padding: 12px 16px;
  border-bottom: 1px solid var(--as-line);
}

.as-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--as-ink);
}

.as-card__sub {
  margin: 3px 0 0;
  font-size: 12px;
  color: var(--as-ink-2);
}

.as-rules {
  padding: 4px 16px 14px;
}

.as-rule {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f4f5f7;
}

.as-rule:last-child {
  border-bottom: none;
}

.as-rule__no {
  flex: 0 0 22px;
  height: 22px;
  margin-top: 4px;
  border-radius: 50%;
  background: #eef2f6;
  color: #344054;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  line-height: 22px;
}

.as-rule__body {
  flex: 1 1 auto;
  min-width: 0;
}

.as-rule__text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  font-size: 13px;
  line-height: 2;
  color: var(--as-ink-2);
}

.as-rule__text strong {
  color: var(--as-ink);
}

.as-rule__effect {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--as-ink-3);
}

.as-inline {
  max-width: 96px;
  flex: 0 0 96px;
}

.as-inline ::v-deep input {
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.as-inline ::v-deep .v-input__slot {
  min-height: 32px !important;
  padding: 0 8px !important;
}

.as-inline ::v-deep input::-webkit-outer-spin-button,
.as-inline ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.as-toggle ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  height: 28px !important;
}

.as-check {
  margin: 0;
  padding: 0;
}

.as-check ::v-deep .v-label {
  font-size: 13px;
  color: var(--as-ink);
  font-weight: 600;
}

.as-scroll {
  overflow-x: auto;
}

.as-table {
  width: 100%;
  border-collapse: collapse;
}

.as-th {
  padding: 8px 14px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--as-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--as-line);
  white-space: nowrap;
}

.as-th--num {
  text-align: right;
}

.as-td {
  padding: 10px 14px;
  font-size: 12.5px;
  color: var(--as-ink-2);
  border-bottom: 1px solid #f4f5f7;
  white-space: nowrap;
}

.as-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.as-td--strong {
  font-weight: 700;
  color: var(--as-ink);
}

@media (max-width: 600px) {
  .as-page {
    padding: 12px 10px 40px;
  }

  .as-field {
    max-width: none;
    flex: 1 1 100%;
  }
}

.as-check-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 12px 16px 4px;
}

.as-check-field {
  max-width: 260px;
  flex: 1 1 180px;
}

.as-td--muted {
  color: var(--as-ink-3);
  font-size: 12px;
}
</style>
